import { createWebHashHistory, createRouter } from "vue-router"
import store from '../store'
import { mapActions } from "vuex"

/* Guest Components */
const LoginComponent = () => import('../components/LoginComponent.vue')
const RegistrationComponent = () => import('../components/RegistrationComponent.vue')
const EmailVerificationComponent = () => import('../components/EmailVerificationComponent.vue')

const InitRecoveryComponent = () => import('../components/PasswordRecovery/InitRecoveryComponent.vue');
const CodeConfirmationComponent = () => import('../components/PasswordRecovery/CodeConfirmationComponent.vue');

/* Layouts */
const MainLayout = () => import('../components/Layouts/MainLayout.vue')
const WebsiteLayout = () => import('../components/Layouts/WebsiteLayout.vue')
const AuthLayout = () => import('../components/Layouts/AuthLayout.vue')
const PageLayout = () => import('../components/Layouts/PageLayout.vue')

/* Authenticated Components */
const DashboardComponent = () => import('../components/DashboardComponent.vue')
const CreateWebsite = () => import('../components/Websites/CreateWebsite.vue')
const WebsiteAssistantComponent = () => import('../components/Websites/WebsiteAssistantComponent.vue')
const EditWebsiteComponent = () => import('../components/Websites/EditWebsiteComponent.vue')
const PagesList = () => import('../components/Pages/PagesListComponent.vue')
const CreatePageComponent = () => import('../components/Pages/CreatePageComponent.vue')
const PageElementsComponent = () => import('../components/Pages/PageElementsComponent.vue')
const PageHeadComponent = () => import('../components/Pages/PageHeadComponent.vue')
const PageStylerComponent = () => import('../components/Pages/PageStylerComponent.vue')
const AccountComponent = () => import('../components/Account/AccountComponent.vue')
const ChangePasswordComponent = () => import('../components/Account/ChangePasswordComponent.vue')
const PaymentMethodComponent = () => import('../components/Account/PaymentMethodComponent.vue')
const TopUpBalanceComponent = () => import('../components/Account/TopUpBalanceComponent.vue')
const AccountNotificationsComponent = () => import('../components/Account/AccountNotificationsComponent.vue')
const BlockStylerComponent = () => import('../components/PageElement/BlockStylerComponent.vue')
const NewElementComponent = () => import('../components/PageElement/NewElementComponent.vue')
const ElementEditComponent = () => import('../components/PageElement/ElementEditComponent.vue')
const ContactRequestListComponent = () => import('../components/BusinessComponents/ContactRequest/ContactRequestsComponent.vue')
const ScheduleListComponent = () => import('../components/BusinessComponents/Schedule/ScheduleListComponent.vue')
const ScheduleFormComponent = () => import('../components/BusinessComponents/Schedule/ScheduleFormComponent.vue')
const AppointmentTypeListComponent = () => import('../components/BusinessComponents/Schedule/AppointmentTypeListComponent.vue')
const AppointmentTypeFormComponent = () => import('../components/BusinessComponents/Schedule/AppointmentTypeFormComponent.vue')
const AppointmentListComponent = () => import('../components/BusinessComponents/Schedule/AppointmentListComponent.vue')
const AppointmentHistoryComponent = () => import('../components/BusinessComponents/Schedule/AppointmentHistoryComponent.vue')
const AppointmentCreateFormComponent = () => import('../components/BusinessComponents/Schedule/AppointmentCreateFormComponent.vue')
const AISuggestedServicesComponent = () => import('../components/BusinessComponents/Schedule/AISuggestedServicesComponent.vue')

const Routes = [
    {
        path: '/',
        component: AuthLayout,
        children: [
            {
                name: "login",
                path: "/",
                component: LoginComponent,
                meta: {
                    middleware: "guest",
                    title: `Login`
                }
            },
            {
                name: "registration",
                path: "/registration",
                component: RegistrationComponent,
                meta: {
                    middleware: "guest",
                    title: `Registration`
                }
            },
            {
                name: "logout",
                path: "/logout",
                component: {
                    beforeEnter: (to, from, next) => {
                        console.log({ from }, { to }, { next });
                        axios
                            .delete("/api/token")
                            .then(({ data }) => {
                                console.log('Token removed successfully', data);
                                store.dispatch('auth/logout'); // Ensure you correctly dispatch the action
                            })
                            .catch(error => {
                                console.log(error.message);
                            })
                            .finally(() => {
                                next({ name: "login" });
                            });
                    }
                },
                meta: {
                    middleware: "auth",
                    title: `Logout`
                }
            },
            {
                path: "/email-verification",
                name: "email.verification",
                component: EmailVerificationComponent,
                meta: {
                    middleware: "guest",
                    title: `Email Verification`
                }
            },
            {
                path: "/recover-password",
                name: "password.recovery.init",
                component: InitRecoveryComponent,
                meta: {
                    middleware: "guest",
                    title: `RecoverPassword`
                }
            },
            {
                path: "/recover-password/:email/code",
                props: true,
                name: "password.recovery.code",
                component: CodeConfirmationComponent,
                meta: {
                    middleware: "guest",
                    title: `Recovery Password`
                }
            },
        ]
    },
    {
        name: "dashboard",
        path: '/',
        component: DashboardComponent,
        meta: {
            middleware: "auth",
            title: `Dashboard`
        }
    },
    {
        name: "account",
        path: '/',
        component: MainLayout,
        props: true,
        children: [
            {
                path: '/account/',
                name: 'account.main',
                component: AccountComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Account data`
                }
            },
            {
                path: '/account/change-password',
                name: 'account.password',
                component: ChangePasswordComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Change account password`
                }
            },
            {
                path: '/account/balance',
                name: 'account.balance',
                component: TopUpBalanceComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Top up balance`
                }
            },
            {
                path: '/account/payment',
                name: 'account.payment',
                component: PaymentMethodComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Update payment method`
                }
            },
            {
                path: '/account/notifications',
                name: 'account.notifications',
                component: AccountNotificationsComponent,
                props: true,
                meta: {
                    middleware: 'auth',
                    title: 'Notifications'
                }
            },
        ],
        meta: {
            title: `Account`,
            middleware: "auth",
        }
    },
    {
        name: "websites",
        path: '/websites',
        component: WebsiteLayout,
        props: true,
        children: [
            {
                path: '/websites/',
                name: 'websites.get',
                component: DashboardComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Your Webs`
                }
            },
            {
                path: '/assistant',
                name: 'website.assistant.get',
                component: WebsiteAssistantComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Website Assistant`
                }
            },
            {
                path: '/websites/create',
                name: 'website.post',
                component: CreateWebsite,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Create a Web`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)',
                name: 'website.get',
                props: true,
                component: PagesList,
                meta: {
                    middleware: "auth",
                    title: `Website Information`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/settings',
                name: 'website.edit',
                component: EditWebsiteComponent,
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Create a Web`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/contact-requests',
                component: ContactRequestListComponent,
                name: "website.contactRequests",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Website Contact Requests`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/schedules',
                component: ScheduleListComponent,
                name: "website.schedules",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Schedules`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/appointment-types',
                component: AppointmentTypeListComponent,
                name: "website.appointment-types",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Appointment Types`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/ai-suggested-services',
                component: AISuggestedServicesComponent,
                name: "website.ai-suggested-services",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `AI Suggested Services`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/schedules/create',
                component: ScheduleFormComponent,
                name: "website.schedules.create",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Create Schedule`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/schedules/:scheduleId(\\d+)/edit',
                component: ScheduleFormComponent,
                name: "website.schedules.edit",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Edit Schedule`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/appointment-types/create',
                component: AppointmentTypeFormComponent,
                name: "website.appointment-types.create",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Create Appointment Type`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/appointment-types/:typeId(\\d+)/edit',
                component: AppointmentTypeFormComponent,
                name: "website.appointment-types.edit",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Edit Appointment Type`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/appointments',
                component: AppointmentListComponent,
                name: "website.appointments",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Appointments`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/appointments/history',
                component: AppointmentHistoryComponent,
                name: "website.appointments.history",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Appointment History`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/appointments/create',
                component: AppointmentCreateFormComponent,
                name: "website.appointments.create",
                props: true,
                meta: {
                    middleware: "auth",
                    title: `Create Appointment`
                }
            },
            {
                path: '/websites/:websiteId(\\d+)/pages',
                component: PageLayout,
                props: true,
                children: [
                    {
                        path: '/websites/:websiteId(\\d+)/pages/create',
                        name: 'pages.post',
                        props: true,
                        component: CreatePageComponent,
                        meta: {
                            middleware: "auth",
                            title: `Create a new Page`
                        }
                    },
                    {
                        path: '/websites/:websiteId(\\d+)/pages/:pageId',
                        name: 'pages.get',
                        props: true,
                        component: PageElementsComponent,
                        meta: {
                            middleware: "auth",
                            title: `Page`
                        }
                    },
                    {
                        path: '/websites/:websiteId(\\d+)/pages/:pageId/head',
                        name: 'pages.head.get',
                        props: true,
                        component: PageHeadComponent,
                        meta: {
                            middleware: "auth",
                            title: `Page SEO`
                        }
                    },
                    {
                        path: '/websites/:websiteId(\\d+)/pages/:pageId/style',
                        name: 'pages.style.get',
                        props: true,
                        component: PageStylerComponent,
                        meta: {
                            middleware: "auth",
                            title: `Page Style`
                        }
                    }]
            },
            {
                path: '/websites/:websiteId(\\d+)/pages/:pageId/element/:elementId/style',
                name: 'pages.element.style',
                props: true,
                component: BlockStylerComponent,
                meta: {
                    middleware: "auth",
                    title: `Element`
                }
            },
            {
                path: '/websites/:websiteId/pages/:pageId/element/:elementId',
                name: 'pages.element.edit',
                props: true,
                component: ElementEditComponent,
                meta: {
                    middleware: "auth",
                    title: `Element`
                }
            },
            {
                path: '/websites/:websiteId/pages/:pageId/element/:elementId/:position',
                name: 'pages.element.post',
                props: true,
                component: NewElementComponent,
                meta: {
                    middleware: "auth",
                    title: `Element`
                }
            },

        ],
    },
]

const router = createRouter({
    history: createWebHashHistory(),
    routes: Routes,
    scrollBehavior(to, from, savedPosition) {
        if (typeof savedPosition !== 'undefined' && savedPosition) {
            return {
                left: savedPosition.left,
                top: savedPosition.top + 30
            };
        }
        return { left: 0, top: 0 }
    }
});

router.beforeEach((to, from, next) => {
    document.title = `${to.meta.title} - ${import.meta.env.PUBLIC_APP_NAME}`
    console.log(from, to, to.meta.middleware, store.state.auth.authenticated);

    if (to.meta.middleware === "auth") {
        if (store.state.auth.authenticated) {
            next();
        } else {
            next({ name: "login" });
        }
    } else {
        next();
    }
})

export default router
